import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays, Loader2, Plus, TriangleAlert, Vote } from 'lucide-react';
import { RequireRole } from '../components/RequireRole';
import { inputClass } from '../components/fields';
import { createMeeting, listMeetings, type MeetingRow, type MeetingType } from '../lib/meetings';
import type { Me } from '../lib/session';

type Load = 'loading' | 'ready' | 'error';

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', published: 'Notice published', in_progress: 'In progress', concluded: 'Concluded', cancelled: 'Cancelled',
};

/**
 * Spec 005 — the meetings index (SC-500/502/503). A member sees published notices; the board also sees
 * drafts and can convene a new meeting. Four states: loading → list → empty → error. WCAG 2.1 AA.
 */
function MeetingsInner({ user }: { user: Me }) {
  const isBoard = user.role === 'board' || user.role === 'admin';
  const [load, setLoad] = useState<Load>('loading');
  const [rows, setRows] = useState<MeetingRow[]>([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ type: 'annual' as MeetingType, title: '', scheduledAt: '' });
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const r = await listMeetings();
    if (r.kind === 'ok') { setRows(r.data); setLoad('ready'); } else { setLoad('error'); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  async function onCreate() {
    if (busy || !form.title.trim() || !form.scheduledAt) return;
    setBusy(true);
    setNotice(null);
    const r = await createMeeting({ type: form.type, title: form.title.trim(), scheduledAt: new Date(form.scheduledAt).toISOString() });
    setBusy(false);
    if (r.kind === 'ok') {
      setCreating(false);
      setForm({ type: 'annual', title: '', scheduledAt: '' });
      setNotice({ tone: 'success', text: 'Meeting created as a draft. Add the agenda, then publish the notice.' });
      void refresh();
    } else if (r.kind === 'invalid') {
      setNotice({ tone: 'error', text: Object.values(r.errors)[0] ?? 'Please check the meeting details.' });
    } else if (r.kind === 'forbidden') {
      setNotice({ tone: 'error', text: 'Only the board can convene a meeting.' });
    } else {
      setNotice({ tone: 'error', text: 'We couldn’t create the meeting. Please try again.' });
    }
  }

  return (
    <section className="mx-auto max-w-4xl px-4 py-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-display text-xs uppercase tracking-[0.2em] text-accent-deep">Democratic governance</p>
          <h1 className="mt-1 font-display text-3xl font-semibold uppercase tracking-[0.08em] text-ink">Meetings</h1>
          <p className="mt-2 font-serif text-base text-muted">Notices, motions, and voting — open to every member (§11–§14).</p>
        </div>
        {isBoard && (
          <button
            type="button"
            onClick={() => setCreating((c) => !c)}
            className="inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg transition-colors duration-150 ease-standard hover:bg-accent-deep focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          >
            <Plus aria-hidden className="h-4 w-4" /> New meeting
          </button>
        )}
      </header>

      {notice && (
        <p role={notice.tone === 'error' ? 'alert' : 'status'} className={`mt-6 rounded-md border px-3 py-2 font-sans text-sm ${notice.tone === 'error' ? 'border-error/40 bg-error/5 text-error' : 'border-success/40 bg-success/5 text-success'}`}>
          {notice.text}
        </p>
      )}

      {isBoard && creating && (
        <div className="mt-6 rounded-md border border-border bg-surface p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="font-sans text-sm text-ink">Type
              <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as MeetingType }))} className={inputClass}>
                <option value="annual">Annual meeting (§11)</option>
                <option value="extra">Extra meeting (§15)</option>
              </select>
            </label>
            <label className="font-sans text-sm text-ink">Date &amp; time
              <input type="datetime-local" value={form.scheduledAt} onChange={(e) => setForm((f) => ({ ...f, scheduledAt: e.target.value }))} className={inputClass} />
            </label>
            <label className="font-sans text-sm text-ink sm:col-span-2">Title
              <input type="text" maxLength={200} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Årsmöte 2027" className={inputClass} />
            </label>
          </div>
          <p className="mt-3 font-sans text-xs text-muted">An annual meeting must be published at least three weeks ahead (§12); an extra meeting at least one week ahead (§15).</p>
          <button type="button" onClick={onCreate} disabled={busy} className="mt-4 inline-flex items-center gap-2 rounded-md bg-accent px-3.5 py-2 font-sans text-sm font-semibold text-bg hover:bg-accent-deep disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
            {busy && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />} Create draft
          </button>
        </div>
      )}

      {load === 'loading' && (
        <div className="mt-10 flex flex-col items-center" aria-live="polite">
          <Loader2 aria-hidden className="h-8 w-8 animate-spin text-accent" />
          <p className="mt-4 font-serif text-lg text-ink">Loading meetings…</p>
        </div>
      )}
      {load === 'error' && (
        <p role="alert" className="mt-8 flex items-start gap-2 rounded-md border border-error/40 bg-error/5 px-3 py-2 font-sans text-sm text-error">
          <TriangleAlert aria-hidden className="mt-0.5 h-4 w-4 shrink-0" /> We couldn’t load the meetings. Please refresh and try again.
        </p>
      )}
      {load === 'ready' && rows.length === 0 && (
        <div className="mt-12 flex flex-col items-center rounded-md border border-dashed border-border bg-surface/40 px-6 py-16 text-center">
          <CalendarDays aria-hidden className="h-9 w-9 text-muted" />
          <p className="mt-4 font-display text-lg font-semibold uppercase tracking-[0.1em] text-ink">No meetings yet</p>
          <p className="mt-2 font-serif text-base text-muted">When the board calls a meeting, its notice will appear here.</p>
        </div>
      )}
      {load === 'ready' && rows.length > 0 && (
        <ul className="mt-8 space-y-4">
          {rows.map((m) => (
            <li key={m.id}>
              <Link to={`/meetings/${m.id}`} className="block rounded-md border border-border bg-surface p-5 transition-colors duration-150 ease-standard hover:border-accent/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-display text-lg font-semibold tracking-wide text-ink">{m.title}</p>
                    <p className="mt-1 font-sans text-sm text-muted">
                      {m.type === 'annual' ? 'Annual meeting' : 'Extra meeting'} · {new Date(m.scheduledAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 font-sans text-xs font-medium text-muted">
                    {m.status === 'in_progress' && <Vote aria-hidden className="h-3.5 w-3.5 text-accent" />}
                    {STATUS_LABEL[m.status] ?? m.status}
                  </span>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default function Meetings() {
  return <RequireRole min="member">{(user) => <MeetingsInner user={user} />}</RequireRole>;
}
